<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue';
import { IonModal, onIonViewWillEnter } from '@ionic/vue';
import { sanitize } from '@/helpers/sanitize';
import { useRoute } from 'vue-router';
import {
  addVrfPhoto,
  deleteVrfPhoto,
  getVrfConfig,
  getVrfResponse,
  submitVrf,
} from '@/services/labo-vrf';
import { DateTime } from 'luxon';
import { useAppModal } from '@/composables/useAppModal';
import { useSnackBar } from '@/composables/useSnackBar';
import { handleBackNavigation } from '@/helpers/navigation';
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import defaultValidators from '@/helpers/default-validators';
import TitleHeader from '@/molecules/TitleHeader.vue';
import BaseButton from '@/atoms/BaseButton.vue';
import BaseCheckbox from '@/atoms/BaseCheckbox.vue';
import BaseLabel from '@/atoms/BaseLabel.vue';
import SkeletonContent from '@/molecules/SkeletonContent.vue';
import TextInput from '@/molecules/TextInput.vue';
import PhoneInput from '@/molecules/PhoneInput.vue';
import RowItemContainer from '@/organisms/RowItemContainer.vue';
import LocalIcon from '@/atoms/LocalIcon.vue';
import CameraBottomSheet from '@/organisms/CameraBottomSheet.vue';
import GenericFileView from '@/atoms/GenericFileView.vue';
import { useBottomSheet } from '@/composables/useBottomSheet';
import type { Pathway } from '@/types/pathway';
import type { FieldConfig, SlideConfig } from '@/types/vrf';

// Valeur sentinelle d'un champ number déclaré « Non collecté » (Réf 18).
const NOT_COLLECTED = 'Non collecté';

const props = defineProps<{ data: Record<string, any> }>();
const emit = defineEmits(['submit', 'canSubmit', 'back', 'next']);

const route = useRoute();
useAppModal();
const snackBar = useSnackBar();
const { open: openBottomSheet } = useBottomSheet();

const slotId = computed(() => route.params.slotId as string);

const formConfig = ref<SlideConfig[]>([]);
const pathway = ref<Pathway | null>(null);
const currentSlide = ref(0);
const isLoading = ref(true);
const isSubmitting = ref(false);
const touchedManual = ref(false);

// Heures d'arrivée / départ : VOLONTAIREMENT vides (fiabilité des données —
// le pro doit saisir l'heure réelle, elles sont obligatoires avant validation).
const proArrivedAt = ref('');
const proDepartedAt = ref('');
const commentProLabo = ref('');
// Photo(s) jointes au VRF — activée par pathway.is_photo_needed. `id` présent =
// déjà persistée côté backend (Azure + row File) : la retirer exige un DELETE
// en plus du splice local. Sans `id` = prise cette session, pas encore soumise.
const photos = ref<{ id?: number; url: string }[]>([]);

// Per-slide storage for ALL field values (including checkbox-multi and datetime)
const slideValues = ref<Record<number, Record<string, any>>>({});

// ─── Validation schema ────────────────────────────────────────────────────────

const isManualType = (type: string) => ['checkbox-multi', 'datetime', 'ratio'].includes(type);

// ─── Champs conditionnels (cahier p.6-8) ──────────────────────────────────────
// Une ligne fille est MASQUÉE quand la condition sur sa mère est remplie
// (« → ne pas afficher » côté admin), ET tant que la mère n'a pas de valeur
// (pas de fille affichée par défaut avant réponse à la question précédente).
function conditionMet(field: FieldConfig, lookup: (model: string) => any): boolean {
  const cond = field.condition;
  const parentModel = field.parent_model;
  if (!parentModel || !cond) return false;
  const raw = lookup(parentModel);
  if (raw == null || raw === '') return true;
  switch (cond.operator) {
    case 'lt':
    case 'gt':
    case 'eq': {
      // mère number : comparaison numérique.
      if (raw == null || raw === '' || cond.value == null) return false;
      const n = Number(raw);
      if (Number.isNaN(n)) return false;
      const target = Number(cond.value);
      if (cond.operator === 'lt') return n < target;
      if (cond.operator === 'gt') return n > target;
      return n === target;
    }
    case 'select':
      // mère radio/select : égalité au label ; mère checkbox-multi : option cochée.
      if (raw == null || raw === '') return false;
      if (typeof raw === 'object') return raw?.[cond.value as string] === true;
      return String(raw) === String(cond.value);
    case 'oui':
      return raw === 'Oui' || raw === true;
    case 'non':
      return raw === 'Non' || raw === false;
    default:
      return false;
  }
}

// Évaluation au moment de la VALIDATION yup : `this.parent` = valeurs live des
// champs standard du slide ; repli sur slideValues pour une mère « manuelle »
// (checkbox-multi/datetime, hors schéma yup).
function fieldHiddenInCtx(field: FieldConfig, ctx: yup.TestContext): boolean {
  const parent = (ctx?.parent ?? {}) as Record<string, any>;
  return conditionMet(field, (m) =>
    parent[m] !== undefined ? parent[m] : slideValues.value[currentSlide.value]?.[m],
  );
}

function buildFieldRule(field: FieldConfig): yup.AnySchema {
  const req = field.isRequired ?? false;
  // « Requis » sensible aux conditions : une fille masquée n'est jamais obligatoire.
  const requiredUnlessHidden = function (this: yup.TestContext, value: any): boolean {
    if (fieldHiddenInCtx(field, this)) return true;
    return value != null && value !== '';
  };
  switch (field.type) {
    case 'input':
    case 'textarea':
      return req
        ? yup.string().nullable().test('required', 'Ce champ est obligatoire', requiredUnlessHidden)
        : yup.string().nullable();
    case 'email':
      return req
        ? yup
            .string()
            .email('Email invalide')
            .nullable()
            .test('required', 'Ce champ est obligatoire', requiredUnlessHidden)
        : yup.string().email('Email invalide').nullable();
    case 'tel':
      if (!req) return yup.string().nullable();
      // Masqué → OK ; sinon délègue format + requis au validateur tel standard.
      return yup.mixed().test('tel', 'Numéro de téléphone invalide', function (v) {
        if (fieldHiddenInCtx(field, this)) return true;
        try {
          defaultValidators.tel.validateSync(v);
          return true;
        } catch (err) {
          return this.createError({ message: (err as any)?.message });
        }
      });
    case 'number': {
      // « Non collecté » (sentinelle) est accepté même si le champ est requis, et
      // les normes min/max sont contrôlées (« erreur si hors normes ») — cahier Réf 18.
      const min = field.min ?? null;
      const max = field.max ?? null;
      // Message exact du cahier (Réf 18, p.35).
      const rangeMsg =
        'La valeur entrée est en dehors des valeurs attendues. Merci de vérifier la valeur saisie.';
      const numOrNc = yup
        .mixed()
        .test('num-or-nc', 'Ce champ doit être un numéro', (v) => {
          if (v === NOT_COLLECTED || v == null || v === '') return true;
          return !Number.isNaN(Number(v));
        })
        .test('within-range', rangeMsg, function (v) {
          // Fille masquée → aucune norme appliquée (valeur non collectée).
          if (fieldHiddenInCtx(field, this)) return true;
          if (v === NOT_COLLECTED || v == null || v === '' || (min == null && max == null))
            return true;
          const n = Number(v);
          if (Number.isNaN(n)) return true; // erreur déjà remontée par num-or-nc
          if (min != null && n < min) return false;
          if (max != null && n > max) return false;
          return true;
        });
      return req
        ? numOrNc.test('required', 'Ce champ est obligatoire', requiredUnlessHidden)
        : numOrNc;
    }
    case 'radio':
      // mixed().nullable() + test requis conditionnel : '' et null rejetés si visible.
      return req
        ? yup.mixed().nullable().test('required', 'Ce champ est obligatoire', requiredUnlessHidden)
        : yup.mixed().nullable();
    default:
      return yup.mixed().nullable();
  }
}

const currentConfig = computed(() => formConfig.value[currentSlide.value]);

const currentSlideValidationSchema = computed(() => {
  const config = currentConfig.value;
  if (!config) return yup.object({});
  const shape: Record<string, yup.AnySchema> = {};
  config.configuration.forEach((field) => {
    if (!isManualType(field.type)) {
      shape[field.model] = buildFieldRule(field);
    }
  });
  return yup.object(shape);
});

const {
  validate,
  errors,
  values: formValues,
  setFieldValue,
  resetForm,
} = useForm({
  validationSchema: currentSlideValidationSchema,
});

// Reset vee-validate form when slide changes
watch(currentSlide, (newSlide) => {
  const saved = slideValues.value[newSlide] ?? {};
  const resetValues: Record<string, any> = {};
  (formConfig.value[newSlide]?.configuration ?? []).forEach((field) => {
    if (!isManualType(field.type)) {
      resetValues[field.model] = saved[field.model] ?? (field.type === 'radio' ? null : '');
    }
  });
  resetForm({ values: resetValues, errors: {} });
});

// Valeur live d'un champ (mère d'une condition) pour le RENDU : vee-validate pour
// les champs standard, slideValues pour les champs manuels du slide courant.
function liveValueOf(model: string): any {
  const vv = (formValues as Record<string, any>)[model];
  if (vv !== undefined) return vv;
  return slideValues.value[currentSlide.value]?.[model];
}
function isFieldHidden(field: FieldConfig): boolean {
  return conditionMet(field, liveValueOf);
}
// Champs affichés du slide courant : lignes filles masquées retirées (cahier p.6-8).
const visibleFields = computed(() =>
  (currentConfig.value?.configuration ?? []).filter((f) => !isFieldHidden(f)),
);

// Quand une fille (champ standard) devient masquée, on VIDE sa valeur : pas de
// donnée fantôme validée/soumise pour un champ non affiché. Les champs manuels
// masqués (checkbox-multi/datetime) sont exclus de la validation et du submit.
watch(
  visibleFields,
  (fields) => {
    const visible = new Set(fields.map((f) => f.model));
    (currentConfig.value?.configuration ?? []).forEach((f) => {
      if (visible.has(f.model) || isManualType(f.type)) return;
      if (formValues[f.model] != null && formValues[f.model] !== '')
        setFieldValue(f.model, f.type === 'radio' ? null : '');
    });
  },
  { flush: 'post' },
);

// ─── Slide state ──────────────────────────────────────────────────────────────

function initSlideValues() {
  formConfig.value.forEach((config, i) => {
    const vals: Record<string, any> = {};
    config.configuration.forEach((field) => {
      if (field.type === 'checkbox-multi') {
        const opts: Record<string, boolean> = {};
        (field.options ?? []).forEach((opt) => {
          opts[opt.label] = opt.checked ?? false;
        });
        vals[field.model] = opts;
      } else if (field.type === 'datetime') {
        // Fiabilité des données : les champs avec une composante HEURE (time /
        // date-time) ne sont PAS pré-remplis — le pro doit saisir l'heure réelle
        // (bloqué au submit si requis et vide). Un champ DATE pur garde le défaut
        // du jour (en heure LOCALE, évite le décalage près de minuit).
        if (field.presentation === 'date') {
          const now = new Date();
          const y = now.getFullYear();
          const mo = String(now.getMonth() + 1).padStart(2, '0');
          const da = String(now.getDate()).padStart(2, '0');
          vals[field.model] = `${y}-${mo}-${da}`;
        } else {
          vals[field.model] = ''; // time / date-time → vide, à saisir
        }
      } else {
        vals[field.model] = field.defaultValue ?? (field.type === 'radio' ? null : '');
      }
    });
    slideValues.value[i] = vals;
  });
}

// Erreur de chargement de la config (fail-closed : on bloque la soumission).
const loadError = ref(false);

/**
 * Charge la config VRF + pathway depuis api-v2 et RÉINITIALISE tout l'état de saisie.
 * Appelé à CHAQUE entrée de vue (cahier Réf 19 : toujours la dernière version en
 * base, pas un cache) et remet à zéro heures/commentaire/valeurs pour
 * éviter toute fuite d'une visite à l'autre (revue M4).
 */
async function loadVrf() {
  isLoading.value = true;
  loadError.value = false;
  // Reset état transverse à la visite.
  currentSlide.value = 0;
  touchedManual.value = false;
  photos.value = [];
  commentProLabo.value = '';
  // Heures NON pré-remplies : le pro saisit les heures réelles (obligatoire).
  proArrivedAt.value = '';
  proDepartedAt.value = '';
  slideValues.value = {};
  formConfig.value = [];
  try {
    const { data } = await getVrfConfig(slotId.value);
    formConfig.value = data?.formConfig ?? [];
    pathway.value = data?.pathway ?? null;
    initSlideValues();

    // Pré-remplissage avec la DERNIÈRE réponse enregistrée (cahier p.38, critères
    // d'acceptation) : reflète aussi les corrections faites par l'IDEC en revue
    // (updateVrf patch `data` en conservant le statut SUBMITTED). Best-effort :
    // un échec réseau ici laisse un formulaire vierge (comportement antérieur),
    // il ne bloque jamais la saisie.
    const visitId = props.data?.flowData?.id;
    if (visitId) {
      try {
        const { data: existing } = await getVrfResponse(visitId);
        if (Array.isArray(existing?.photos)) photos.value = existing.photos;
        if (existing?.exists) {
          applyStoredData(existing.data);
          if (existing.pro_arrived_at) proArrivedAt.value = extractTime(existing.pro_arrived_at);
          if (existing.pro_departed_at) proDepartedAt.value = extractTime(existing.pro_departed_at);
          if (existing.comment_pro_labo) commentProLabo.value = existing.comment_pro_labo;
        }
      } catch (e) {
        console.error(e);
      }
    }

    // Initialise the vee-validate form for slide 0 (après hydratation éventuelle)
    const firstSlideValues: Record<string, any> = {};
    (formConfig.value[0]?.configuration ?? []).forEach((field) => {
      if (!isManualType(field.type)) {
        firstSlideValues[field.model] =
          slideValues.value[0]?.[field.model] ?? (field.type === 'radio' ? null : '');
      }
    });
    resetForm({ values: firstSlideValues, errors: {} });
  } catch (e) {
    console.error(e);
    loadError.value = true;
    pathway.value = null;
    // 403 = le pro n'est pas assigné à ce créneau (garde-fou api-v2) : message
    // dédié, pas un faux « problème de connexion ».
    const status = (e as { response?: { status?: number } })?.response?.status;
    void snackBar.show({
      text:
        status === 403
          ? "Vous n'êtes pas assigné à ce créneau. Impossible d'ouvrir le compte-rendu."
          : 'Impossible de charger le compte-rendu. Vérifiez votre connexion et réessayez.',
    });
  } finally {
    isLoading.value = false;
  }
}

// Premier montage : chargement initial. onIonViewWillEnter recharge aux ré-entrées
// suivantes (on saute le tout premier fire qui coïncide avec le montage).
let firstEnter = true;
onMounted(loadVrf);
onIonViewWillEnter(() => {
  if (firstEnter) {
    firstEnter = false;
    return;
  }
  void loadVrf();
});

const isFirstSlide = computed(() => currentSlide.value === 0);
const isLastSlide = computed(() => currentSlide.value === formConfig.value.length - 1);
const slideCount = computed(() => formConfig.value.length);

const visitTitle = computed(() => {
  const name = props.data?.flowData?.name ?? '';
  const step = props.data?.flowData?.step ?? '';
  return [name, step].filter(Boolean).join(' - ');
});

// Cahier Réf 28 : nom du document selon le type de parcours.
const pathwayType = computed(() => pathway.value?.type_parcours ?? null);
const documentTitle = computed(() =>
  pathwayType.value === 'ECD' ? 'VRF' : 'Compte-rendu de visite',
);

// Affichage en grand d'une photo jointe (tap sur la miniature).
const isPhotoModalOpen = ref(false);
const selectedPhoto = ref<{ id?: number; url: string } | null>(null);
function openPhoto(photo: { id?: number; url: string }) {
  selectedPhoto.value = photo;
  isPhotoModalOpen.value = true;
}
function closePhotoModal() {
  isPhotoModalOpen.value = false;
  selectedPhoto.value = null;
}

// Prise de photo (app mobile) : activée par le réglage global du parcours.
const canTakePhoto = computed(() => !!pathway.value?.is_photo_needed);
async function openCamera() {
  const { data } = await openBottomSheet(CameraBottomSheet, { withUsePhoto: true });
  if (data?.photo) photos.value.push({ url: data.photo });
  if (Array.isArray(data?.photos))
    photos.value.push(...data.photos.map((url: string) => ({ url })));
}
// Une photo avec `id` est déjà persistée (Azure + row File) : il faut la
// supprimer côté backend en plus du retrait local, sinon elle réapparaît au
// prochain chargement du VRF (loadVrf la re-tire de la réponse GET).
async function removePhoto(index: number) {
  const target = photos.value[index];
  photos.value.splice(index, 1);
  const visitId = props.data?.flowData?.id;
  if (!target?.id || !visitId) return;
  try {
    await deleteVrfPhoto(visitId, target.id);
  } catch (e) {
    console.error(e);
    void snackBar.show({ text: 'Impossible de supprimer la photo. Réessayez.' });
    photos.value.splice(index, 0, target);
  }
}

// Cahier Réf 13 : encarts (conseil = bleu / instruction = jaune) du slide courant.
const currentEncarts = computed(() =>
  (currentConfig.value?.encarts ?? []).filter((e) => e?.text?.trim()),
);

// Cahier Réf 18 : gestion « Non collecté » d'un champ number (valeur sentinelle).
function isNotCollected(field: FieldConfig): boolean {
  return formValues[field.model] === NOT_COLLECTED;
}
function toggleNotCollected(field: FieldConfig, checked: boolean) {
  setFieldValue(field.model, checked ? NOT_COLLECTED : '');
}

// Ratio (ex. tension « 12/8 ») : la valeur est stockée « a/b ». On expose chaque
// part séparément pour deux champs de saisie, et on recompose la chaîne.
function ratioPart(model: string, idx: 0 | 1): string {
  return String(formValues[model] ?? '').split('/')[idx] ?? '';
}
function setRatioPart(model: string, idx: 0 | 1, value: string) {
  const parts = String(formValues[model] ?? '').split('/');
  const a = idx === 0 ? value : (parts[0] ?? '');
  const b = idx === 1 ? value : (parts[1] ?? '');
  setFieldValue(model, a || b ? `${a}/${b}` : '');
}

// Manual fields (checkbox-multi, datetime) still validated separately — hors
// filles masquées (cahier p.6-8 : un champ non affiché n'est jamais requis).
const currentManualFields = computed(() =>
  (currentConfig.value?.configuration ?? []).filter(
    (f) => isManualType(f.type) && !isFieldHidden(f),
  ),
);

function isManualFieldInvalid(field: FieldConfig): boolean {
  if (isFieldHidden(field)) return false;
  if (!touchedManual.value || !field.isRequired) return false;
  const val = slideValues.value[currentSlide.value]?.[field.model];
  if (field.type === 'checkbox-multi') return !Object.values(val ?? {}).some(Boolean);
  // ratio : les DEUX parts « a/b » doivent être renseignées.
  if (field.type === 'ratio') {
    const [a, b] = String(val ?? '').split('/');
    return !a?.trim() || !b?.trim();
  }
  return val === '' || val === null || val === undefined;
}

// Save vee-validate values back into slideValues before navigating
function saveCurrentSlideValues() {
  const veeValues: Record<string, any> = {};
  (currentConfig.value?.configuration ?? []).forEach((field) => {
    if (!isManualType(field.type)) {
      veeValues[field.model] = formValues[field.model];
    }
  });
  slideValues.value[currentSlide.value] = {
    ...slideValues.value[currentSlide.value],
    ...veeValues,
  };
}

async function nextSlide() {
  touchedManual.value = true;
  if (isFirstSlide.value && !proArrivedAt.value) return;

  const { valid } = await validate();
  if (!valid) return;

  const manualInvalid = currentManualFields.value.some((f) => isManualFieldInvalid(f));
  if (manualInvalid) return;

  saveCurrentSlideValues();
  touchedManual.value = false;
  currentSlide.value++;
}

function prevSlide() {
  saveCurrentSlideValues();
  touchedManual.value = false;
  if (currentSlide.value > 0) currentSlide.value--;
}

const onBack = computed(() =>
  currentSlide.value > 0 ? () => prevSlide() : () => handleBackNavigation(),
);

function getDatetimeInputType(presentation?: string): string {
  if (presentation === 'date') return 'date';
  if (presentation === 'time') return 'time';
  return 'datetime-local';
}

function buildTimeWithTz(timeStr: string): string {
  const [hours, minutes] = timeStr.split(':').map(Number);
  const iso = DateTime.now().set({ hour: hours, minute: minutes }).toISO()!;
  return iso.substring(11);
}

function mapFieldValue(field: FieldConfig, rawValue: any): any {
  if (field.type === 'radio') {
    return {
      value: rawValue,
      options: (field.options ?? []).map((o) => o.label),
    };
  }
  if (field.type === 'datetime' && typeof rawValue === 'string' && rawValue) {
    return DateTime.fromISO(rawValue);
  }
  return rawValue;
}

// ─── Pré-remplissage depuis la dernière réponse enregistrée (cahier p.38) ─────
// Inverse de `mapFieldValue` : reconstitue une valeur de saisie (slideValues)
// depuis la valeur stockée en base (format produit au submit).
function restoreFieldValue(field: FieldConfig, stored: any): any {
  if (field.type === 'radio') return stored?.value ?? null;
  if (field.type === 'datetime') {
    if (typeof stored !== 'string' || !stored) return '';
    // ISO local (Luxon.toJSON()) → format attendu par l'<input> selon la présentation.
    if (field.presentation === 'date') return stored.slice(0, 10);
    if (field.presentation === 'time') return stored.slice(11, 16);
    return stored.slice(0, 16);
  }
  return stored;
}

/** Extrait « HH:mm » d'une heure-seule stockée (ex. "09:30:00.000+02:00"). */
function extractTime(v: string): string {
  const m = String(v).match(/(\d{2}):(\d{2})/);
  return m ? `${m[1]}:${m[2]}` : '';
}

/**
 * Applique les données d'une réponse VRF déjà enregistrée sur `slideValues`.
 * Corrélation par (formConfigId, label, type) — le format soumis ne conserve
 * pas `field.model`. Une fille masquée au submit précédent est simplement
 * absente du tableau stocké : son défaut (initSlideValues) reste inchangé.
 */
function applyStoredData(dataArr: unknown): void {
  if (!Array.isArray(dataArr)) return;
  dataArr.forEach((entry: any) => {
    const slideIndex = formConfig.value.findIndex((c) => c.id === entry?.formConfigId);
    if (slideIndex === -1) return;
    const config = formConfig.value[slideIndex];
    const storedFields: any[] = Array.isArray(entry.form) ? entry.form : [];
    config.configuration.forEach((field) => {
      const match = storedFields.find((f) => f?.label === field.label && f?.type === field.type);
      if (!match) return;
      if (field.type === 'checkbox-multi') {
        const current = slideValues.value[slideIndex]?.[field.model] ?? {};
        const restored: Record<string, boolean> = { ...current };
        if (match.value && typeof match.value === 'object') {
          Object.keys(restored).forEach((k) => {
            if (k in match.value) restored[k] = !!match.value[k];
          });
        }
        slideValues.value[slideIndex][field.model] = restored;
      } else {
        slideValues.value[slideIndex][field.model] = restoreFieldValue(field, match.value);
      }
    });
  });
}

async function handleSubmit() {
  touchedManual.value = true;
  // Fail-closed : si la config n'a pas pu être chargée (donc `pathway`/type inconnus),
  // on ne soumet pas (revue B3/M3) — la validation ECD ne serait pas fiable.
  if (loadError.value) {
    void snackBar.show({
      text: 'Le compte-rendu n’a pas pu être chargé. Rechargez la page avant de valider.',
    });
    return;
  }
  const visitId = props.data?.flowData?.id;
  if (!visitId) {
    void snackBar.show({
      text: 'Visite introuvable. Revenez en arrière puis rouvrez le compte-rendu.',
    });
    return;
  }
  // Heures d'arrivée ET de départ obligatoires (VRF mono-slide : les deux sont
  // sur le même écran, l'arrivée n'a pas été bloquée par un changement de slide).
  if (!proArrivedAt.value || !proDepartedAt.value) return;
  const { valid } = await validate();
  if (!valid) return;

  const manualInvalid = currentManualFields.value.some((f) => isManualFieldInvalid(f));
  if (manualInvalid) return;

  saveCurrentSlideValues();

  try {
    isSubmitting.value = true;

    // Cahier Réf 13 : les encarts (conseil/instruction) sont un support d'aide à
    // la saisie et ne doivent PAS figurer dans le VRF soumis → on n'envoie que les
    // champs `configuration`, jamais `config.encarts`.
    const formData = formConfig.value.map((config, index) => {
      const vals = slideValues.value[index] ?? {};
      // Cahier p.6-8 : une ligne fille masquée (condition mère remplie) n'est PAS
      // collectée → on l'exclut du VRF soumis (visibilité évaluée par slide).
      const lookup = (m: string) => vals[m];
      return {
        medical_element: config.MedicalElement,
        formConfigId: config.id,
        comment_admin_medical_element: config.comment_admin_medical_element,
        form: config.configuration
          .filter((field) => !conditionMet(field, lookup))
          .map((field) => ({
            label: field.label,
            type: field.type,
            value: mapFieldValue(field, vals[field.model]),
            presentation: field.presentation,
            // Unité (number) : transmise pour être rendue dans le VRF, pas
            // seulement dans l'APP (cahier p.35 : « afficher dans l'APP ET le VRF »).
            unit: field.unit,
          })),
      };
    });

    // Submit via api-v2 (pro_id dérivé du JWT côté back, plus dans le body).
    await submitVrf(visitId, {
      pathway_id: pathway.value?.id ?? formConfig.value[0]?.pathway_id,
      data: formData,
      pro_arrived_at: buildTimeWithTz(proArrivedAt.value),
      pro_departed_at: buildTimeWithTz(proDepartedAt.value),
      comment_pro_labo: commentProLabo.value,
    });

    // Photos envoyées APRÈS le submit, une requête par photo (pas dans le body
    // du submit) : un seul payload avec plusieurs photos base64 dépassait la
    // limite body-parser dès 2-3 photos. Seules celles SANS id sont neuves
    // (prises cette session) : celles avec id sont déjà uploadées.
    for (const p of photos.value.filter((p) => !p.id)) {
      try {
        const { data: uploaded } = await addVrfPhoto(visitId, p.url);
        p.id = uploaded.id;
      } catch (e) {
        console.error(e);
      }
    }

    emit('submit');
  } catch (e) {
    console.error(e);
    // Plus d'échec silencieux : le pro doit savoir que le VRF n'est PAS parti
    // (réseau instable en visite) — sinon perte de données (revue B4).
    const status = (e as { response?: { status?: number } })?.response?.status;
    void snackBar.show({
      text:
        status === 403
          ? "Vous n'êtes plus assigné à ce créneau. Le compte-rendu n'a pas été envoyé."
          : 'L’envoi du compte-rendu a échoué. Rien n’a été enregistré, merci de réessayer.',
      duration: 4000,
    });
  } finally {
    isSubmitting.value = false;
  }
}

// Radio options formatted for RowItemContainer
function radioRowItems(field: FieldConfig) {
  const options = field.options ?? [];
  // Cahier Réf 18 : plus d'espace si >2 choix ou texte long (cercles non collés).
  const spaced = options.length > 2 || options.some((o) => (o.label ?? '').length > 20);
  return options.map((o) => ({
    id: String(o.value ?? o.label),
    value: o.value ?? o.label,
    title: o.label,
    class: spaced ? 'py-5' : 'py-3',
  }));
}

// Checkbox-multi options formatées pour RowItemContainer (même style que le radio).
function checkboxRowItems(field: FieldConfig) {
  const options = field.options ?? [];
  const spaced = options.length > 2 || options.some((o) => (o.label ?? '').length > 20);
  return options.map((o) => ({
    id: o.label,
    value: o.label,
    title: o.label,
    class: spaced ? 'py-5' : 'py-3',
  }));
}

// CheckboxRowItem attend un modelValue `string[]`, mais slideValues stocke un
// objet `{ [label]: boolean }` (soumis tel quel au submit) : conversion aller-retour.
function checkboxSelectedValues(field: FieldConfig): string[] {
  const val = slideValues.value[currentSlide.value]?.[field.model] ?? {};
  return Object.keys(val).filter((k) => val[k]);
}
function setCheckboxSelectedValues(field: FieldConfig, selected: string[] | boolean | undefined) {
  const values = Array.isArray(selected) ? selected : [];
  const current = slideValues.value[currentSlide.value]?.[field.model] ?? {};
  const updated: Record<string, boolean> = {};
  Object.keys(current).forEach((k) => {
    updated[k] = values.includes(k);
  });
  slideValues.value[currentSlide.value][field.model] = updated;
}

// Cahier Réf 18 : cartouche texte (input/textarea) qui s'agrandit à la saisie.
function autoGrow(e: Event) {
  const el = e.target as HTMLTextAreaElement;
  el.style.height = 'auto';
  el.style.height = `${el.scrollHeight}px`;
}
function onCartoucheInput(model: string, e: Event) {
  setFieldValue(model, (e.target as HTMLTextAreaElement).value);
  autoGrow(e);
}
</script>

<template>
  <div>
    <TitleHeader :on-back="onBack" :tabs="[]" :title="documentTitle" />

    <SkeletonContent v-if="isLoading" />

    <!-- Fail-closed : erreur de chargement → pas de formulaire, on propose de réessayer. -->
    <div v-else-if="loadError" class="p-6 text-center">
      <p class="text-primary">Impossible de charger le compte-rendu. Vérifiez votre connexion.</p>
      <BaseButton :on-click="loadVrf" class="mt-4" label="Réessayer" />
    </div>

    <template v-else>
      <!-- Slide indicator -->
      <div v-if="slideCount > 1" class="flex justify-center mb-4">
        <span class="text-sm text-grey-500">{{ currentSlide + 1 }} / {{ slideCount }}</span>
      </div>

      <div class="space-y-4">
        <!-- Visit title on first slide -->
        <p v-if="isFirstSlide && visitTitle" class="text-base font-semibold">{{ visitTitle }}</p>

        <!-- Pathway intro comment on first slide -->
        <div
          v-if="isFirstSlide && pathway?.comment_admin_start_labo"
          class="text-sm text-primary"
          v-html="sanitize(pathway.comment_admin_start_labo)"
        />

        <!-- pro_arrived_at on first slide -->
        <div v-if="isFirstSlide">
          <BaseLabel color="primary" preset="medium" text="Heure de début de la visite *" />
          <div class="mt-1 w-full overflow-hidden rounded-lg bg-grey-50">
            <input v-model="proArrivedAt" class="time-input w-full p-2 text-primary" type="time" />
          </div>
          <p v-if="touchedManual && !proArrivedAt" class="mt-1 text-xs text-error-solid">
            Ce champ est obligatoire
          </p>
        </div>

        <!-- Medical element title -->
        <p class="font-semibold text-primary">{{ currentConfig?.MedicalElement?.name }}</p>

        <!-- Medical element comment -->
        <div
          v-if="currentConfig?.comment_admin_medical_element"
          class="text-sm text-primary"
          v-html="sanitize(currentConfig.comment_admin_medical_element)"
        />

        <!-- Encarts conseil (bleu) / instruction (jaune) — cahier Réf 13.
             Support d'aide à la saisie ; jamais inclus dans le VRF soumis. -->
        <div
          v-for="(encart, ei) in currentEncarts"
          :key="`encart-${ei}`"
          class="rounded-lg border p-3 text-sm"
          :class="
            encart.type === 'instruction'
              ? 'border-yellow-200 bg-yellow-50 text-yellow-900'
              : 'border-blue-200 bg-blue-50 text-blue-900'
          "
          v-html="sanitize(encart.text)"
        />

        <!-- Champs, dans l'ordre. Barre grise entre chaque item (cahier Réf 18). -->
        <div
          v-for="field in visibleFields"
          :key="field.model"
          class="border-b border-grey-200 pb-4 last:border-b-0 last:pb-0"
        >
          <!-- champs texte simple + grand texte : cartouche gris clair, auto-grow (Réf 18) -->
          <div v-if="field.type === 'input' || field.type === 'textarea'">
            <BaseLabel
              :text="field.label + (field.isRequired ? ' *' : '')"
              color="primary"
              preset="medium"
            />
            <textarea
              :value="formValues[field.model] ?? ''"
              :placeholder="field.placeholder"
              rows="1"
              class="mt-1 w-full resize-none overflow-hidden rounded-lg bg-grey-50 p-3 text-primary placeholder:text-grey-400"
              :class="errors[field.model] ? 'ring-1 ring-error-solid' : ''"
              @input="onCartoucheInput(field.model, $event)"
            />
            <p v-if="errors[field.model]" class="mt-1 text-xs text-error-solid">
              {{ errors[field.model] }}
            </p>
          </div>

          <!-- email -->
          <TextInput
            v-else-if="field.type === 'email'"
            :id="field.model"
            :model-value="formValues[field.model] ?? ''"
            :label="field.label + (field.isRequired ? ' *' : '')"
            :placeholder="field.placeholder"
            :error="errors[field.model]"
            :destructive="!!errors[field.model]"
            type="email"
            class="w-full"
            @update:model-value="setFieldValue(field.model, $event)"
          />

          <!-- number : label noir + unité grise dessous + case sous le label (cahier Réf 18) -->
          <div v-else-if="field.type === 'number'">
            <BaseLabel
              :text="field.label + (field.isRequired ? ' *' : '')"
              color="primary"
              preset="medium"
            />
            <p v-if="field.unit" class="text-xs text-grey-500">
              {{ field.unit }}
            </p>
            <input
              :value="isNotCollected(field) ? '' : (formValues[field.model] ?? '')"
              :disabled="isNotCollected(field)"
              :placeholder="field.placeholder"
              type="number"
              inputmode="decimal"
              class="mt-1 w-24 rounded-lg bg-grey-50 p-2 text-center text-primary placeholder:text-grey-400 disabled:bg-grey-50"
              @input="setFieldValue(field.model, ($event.target as HTMLInputElement).value)"
            />
            <div v-if="field.allowNotCollected" class="mt-2 flex items-center gap-2">
              <BaseCheckbox
                :id="`${field.model}-not-collected`"
                :model-value="isNotCollected(field)"
                @update:model-value="toggleNotCollected(field, !!$event)"
              />
              <label :for="`${field.model}-not-collected`" class="text-sm text-primary">
                Non collecté
              </label>
            </div>
            <p v-if="errors[field.model]" class="mt-1 text-xs text-error-solid">
              {{ errors[field.model] }}
            </p>
          </div>

          <!-- ratio : deux nombres « a / b » (ex. tension 12/8) -->
          <div v-else-if="field.type === 'ratio'">
            <div class="flex items-start justify-between gap-3">
              <div class="min-w-0">
                <BaseLabel
                  :text="field.label + (field.isRequired ? ' *' : '')"
                  color="primary"
                  preset="medium"
                />
                <p v-if="field.unit" class="text-xs text-grey-500">
                  {{ field.unit }}
                </p>
              </div>
              <div class="flex shrink-0 items-center gap-1">
                <input
                  :value="ratioPart(field.model, 0)"
                  :placeholder="field.placeholder"
                  type="number"
                  inputmode="decimal"
                  class="w-16 rounded-lg bg-grey-50 p-2 text-center text-primary placeholder:text-grey-400"
                  @input="setRatioPart(field.model, 0, ($event.target as HTMLInputElement).value)"
                />
                <span class="text-grey-500">/</span>
                <input
                  :value="ratioPart(field.model, 1)"
                  type="number"
                  inputmode="decimal"
                  class="w-16 rounded-lg bg-grey-50 p-2 text-center text-primary placeholder:text-grey-400"
                  @input="setRatioPart(field.model, 1, ($event.target as HTMLInputElement).value)"
                />
              </div>
            </div>
            <p v-if="isManualFieldInvalid(field)" class="mt-1 text-xs text-error-solid">
              Merci de renseigner ce champ
            </p>
          </div>

          <!-- tel -->
          <PhoneInput
            type="tel"
            v-else-if="field.type === 'tel'"
            :id="field.model"
            :model-value="formValues[field.model] ?? ''"
            :label="field.label + (field.isRequired ? ' *' : '')"
            :error="errors[field.model]"
            :destructive="!!errors[field.model]"
            class="w-full"
            @update:model-value="setFieldValue(field.model, $event)"
          />

          <!-- radio : même style liste que le checkbox-multi (Réf 18) -->
          <div v-else-if="field.type === 'radio'">
            <BaseLabel
              :text="field.label + (field.isRequired ? ' *' : '')"
              color="primary"
              preset="medium"
            />
            <RowItemContainer
              :model-value="formValues[field.model]"
              :row-items="radioRowItems(field)"
              row-item-type="RadioRowItem"
              allow-deselect
              @update:model-value="setFieldValue(field.model, $event)"
            />
            <p v-if="errors[field.model]" class="mt-1 text-xs text-error-solid">
              {{ errors[field.model] }}
            </p>
          </div>

          <!-- checkbox-multi : même style liste que le radio (Réf 18) -->
          <div v-else-if="field.type === 'checkbox-multi'">
            <BaseLabel
              :text="field.label + (field.isRequired ? ' *' : '')"
              color="primary"
              preset="medium"
            />
            <RowItemContainer
              checkbox-type="default"
              :model-value="checkboxSelectedValues(field)"
              :row-items="checkboxRowItems(field)"
              row-item-type="CheckboxRowItem"
              @update:model-value="setCheckboxSelectedValues(field, $event)"
            />
            <p v-if="isManualFieldInvalid(field)" class="mt-1 text-xs text-error-solid">
              Merci de sélectionner au moins une option
            </p>
          </div>

          <!-- datetime -->
          <div v-else-if="field.type === 'datetime'">
            <BaseLabel
              :text="field.label + (field.isRequired ? ' *' : '')"
              color="primary"
              preset="medium"
            />
            <input
              v-model="slideValues[currentSlide][field.model]"
              :type="getDatetimeInputType(field.presentation)"
              class="mt-1 w-full rounded-lg bg-grey-50 p-2 text-center text-primary"
            />
            <p v-if="isManualFieldInvalid(field)" class="mt-1 text-xs text-error-solid">
              Merci de renseigner ce champ
            </p>
          </div>

          <!-- Encarts « par ligne » (cahier p.28) : conseil (bleu) / instruction
               (jaune) rattachés au champ. Support d'aide à la saisie ; jamais
               inclus dans le VRF soumis (comme les encarts de section). -->
          <div
            v-for="(encart, ei) in (field.encarts ?? []).filter((e) => e?.text?.trim())"
            :key="`${field.model}-encart-${ei}`"
            class="mt-2 rounded-lg border p-3 text-sm"
            :class="
              encart.type === 'instruction'
                ? 'border-yellow-200 bg-yellow-50 text-yellow-900'
                : 'border-blue-200 bg-blue-50 text-blue-900'
            "
            v-html="sanitize(encart.text)"
          />
        </div>

        <!-- pro_departed_at on last slide -->
        <div v-if="isLastSlide">
          <BaseLabel color="primary" preset="medium" text="Heure de fin de la visite *" />
          <div class="mt-1 w-full overflow-hidden rounded-lg bg-grey-50">
            <input v-model="proDepartedAt" class="time-input w-full p-2 text-primary" type="time" />
          </div>
          <p v-if="touchedManual && !proDepartedAt" class="mt-1 text-xs text-error-solid">
            Ce champ est obligatoire
          </p>
        </div>

        <!-- comment_pro_labo on last slide : activé par pathway.vrf_comment_field_enabled (toggle admin). -->
        <div v-if="isLastSlide && pathway?.vrf_comment_field_enabled">
          <BaseLabel color="primary" preset="medium" text="Commentaire" />
          <textarea
            v-model="commentProLabo"
            class="mt-1 min-h-24 w-full rounded-lg bg-grey-50 p-2 text-primary"
            placeholder="Écrivez vos commentaires"
          />
        </div>

        <!-- Prise de photo (app mobile) : activée par pathway.is_photo_needed, sur le dernier slide. -->
        <div v-if="isLastSlide && canTakePhoto">
          <BaseLabel color="primary" preset="medium" text="Photos" />
          <BaseButton
            class="mt-2"
            label="Ajouter une photo"
            type="secondary"
            icon-name="camera"
            :on-click="openCamera"
          />
          <div v-if="photos.length" class="mt-4 grid grid-cols-3 gap-2">
            <div
              v-for="(p, pi) in photos"
              :key="p.id ?? `photo-${pi}`"
              class="relative aspect-square overflow-hidden rounded-lg bg-grey-100"
            >
              <img
                :src="p.url"
                alt="Photo jointe"
                class="h-full w-full object-cover"
                @click="openPhoto(p)"
              />
              <button
                type="button"
                class="absolute top-1 right-1 flex h-6 w-6 items-center justify-center rounded-full bg-grey-50 text-brand-solid"
                @click="removePhoto(pi)"
              >
                <LocalIcon name="trash" size="xs" />
              </button>
            </div>
          </div>
        </div>

        <!-- Pathway end comment on last slide -->
        <div
          v-if="isLastSlide && pathway?.comment_admin_end_labo"
          class="text-sm text-primary"
          v-html="sanitize(pathway.comment_admin_end_labo)"
        />
      </div>

      <!-- Navigation -->
      <div class="mt-6 mb-6">
        <BaseButton
          v-if="!isLastSlide"
          :on-click="nextSlide"
          class="w-full"
          label="Suivant"
          second-icon-name="arrow-right"
        />
        <BaseButton
          v-else
          :loading="isSubmitting"
          :on-click="handleSubmit"
          class="w-full"
          label="Envoyer"
          second-icon-name="arrow-right"
        />
      </div>
    </template>
    <ion-modal :is-open="isPhotoModalOpen" class="h-screen" @didDismiss="closePhotoModal">
      <TitleHeader
        :on-back="closePhotoModal"
        class="ml-2 mr-2"
        style="padding-top: env(safe-area-inset-top, 20px)"
      />
      <GenericFileView
        v-if="selectedPhoto?.url"
        :show-zoom-controller="false"
        :src="selectedPhoto.url"
        class="h-screen"
        file-type="image"
      />
    </ion-modal>
  </div>
</template>

<style scoped>
.time-input::-webkit-calendar-picker-indicator {
  filter: invert(1) brightness(0);
}
</style>
